import { Button } from "@/components/ui/button";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Loader2, Star } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "~/components/ui/dialog";

import { LoadingHoc } from "@/components/loading-hoc";
import { env } from "@/env";
import { m } from "@/paraglide/messages";
import { localizeHref } from "@/paraglide/runtime";
import { useCountDown } from "ahooks";
import { cn } from "../utils";
import { commonLogin, getOAuthUrl, sendEmailLink } from "./api";
import { isAuthRoute } from "./routes";
import { AUTH_EVENT_TYPE, authChannel } from "./store";

interface TelegramAuthResponse {
	id: number;
	username?: string;
	first_name?: string;
	last_name?: string;
	photo_url: string;
	auth_date: number;
	hash: string;
}

function isValidEmail(email: string) {
	// 这是一个常用的、相对简单的 email 正则表达式
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
}

export const LoginModal = NiceModal.create(() => {
	const modal = useModal();

	const [email, setEmail] = useState("");

	const [targetDate, setTargetDate] = useState<number>();
	const [countdown] = useCountDown({
		targetDate,
	});

	const [hasEmailError, setHasEmailError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	return (
		<Dialog
			open={modal.visible}
			onOpenChange={(open) => {
				if (!open) {
					if (isAuthRoute(localizeHref(location.pathname))) {
						window.location.href = localizeHref("/");
					}
					modal.hide();
				}
			}}
		>
			<DialogContent className="md:min-w-[520px] h-[74vh]">
				<DialogHeader className="hidden md:block">
					<DialogTitle className="items-center gap-2 flex">
						<img src="/logo.svg" alt="logo" className="h-10" />
						{/* <div className="text-lg font-bold text-main1">PeachLust</div> */}
					</DialogTitle>
				</DialogHeader>
				<div className="flex flex-col items-center pr-4 md:pr-6 md:mt-0 mt-24">
					<div className="text-lg mt-12">
						<div className="items-center gap-2 flex md:hidden mb-3">
							<img src="/logo.png" alt="logo" className="size-10" />
							<div className="text-xl font-bold text-main1">PeachLust</div>
						</div>
						<m.login_modal_title app_name="PeachLust" />
					</div>
					<div className="mt-7 flex items-center gap-2 w-full md:w-[420px] relative px-4 md:px-0">
						<input
							placeholder={m.login_modal_input_placeholder()}
							className="w-full border-l2 border rounded-full bg-bg1 px-4 py-2 text-sm text-t1 placeholder:text-t3"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
						/>

						<Button
							onClick={() => {
								if (!email || countdown > 0) {
									return;
								}
								if (isValidEmail(email)) {
									setHasEmailError(false);
								} else {
									setHasEmailError(true);
									return;
								}
								setTargetDate(Date.now() + 60 * 1000);
								setIsLoading(true);
								sendEmailLink(email)
									.then((res) => {
										if (res.code === 0) {
											toast.success(m.login_modal_send_email_link());
										} else {
											setTargetDate(undefined);
										}
									})
									.finally(() => {
										setIsLoading(false);
									});
							}}
							className={cn(
								"md:w-30 w-20 rounded-full h-9 text-sm absolute right-0 transition-all",
								email ? "block" : "hidden",
							)}
						>
							{isLoading ? (
								<Loader2 className="size-4.5 animate-spin mx-auto" />
							) : countdown > 0 ? (
								`${Math.round(countdown / 1000)}S`
							) : (
								m.common_continue()
							)}
						</Button>
						{hasEmailError && (
							<div
								className={"pl-4 -mb-16 text-left text-main1 text-xs absolute "}
							>
								{m.login_modal_email_error()}
							</div>
						)}
					</div>

					<div className="md:mt-12 mt-auto flex items-center gap-6 ">
						{[
							{ key: "google", icon: "/assets/icons/google.svg" },
							{ key: "twitter", icon: "/assets/icons/twitter.svg" },
							{ key: "discord", icon: "/assets/icons/discord.svg" },
							{ key: "telegram", icon: "/assets/icons/telegram.svg" },
						].map((it) => {
							return (
								<LoadingHoc key={it.key}>
									{({ isLoading, loadingIcon, wrapPromise }) => (
										<div
											className="flex items-center justify-center w-12 h-12 rounded-full bg-bg2 cursor-pointer"
											onClick={async () => {
												if (it.key === "telegram") {
													// @ts-ignore
													window?.Telegram?.Login?.auth(
														{
															bot_id: env.VITE_TG_BOT_ID,
															request_access: true,
														},
														async (data: TelegramAuthResponse) => {
															if (data?.hash) {
																const rt = await wrapPromise(
																	commonLogin("telegram", data),
																);
																if (rt.code === 0 && rt.data.token) {
																	localStorage.setItem(
																		"AUTH_TOKEN",
																		rt.data.token,
																	);
																	authChannel.postMessage({
																		type: AUTH_EVENT_TYPE.LOGIN,
																	});
																	window.location.reload();
																}
															} else {
																// toast.error(m.login_modal_login_error());
															}
														},
													);
													return;
												}

												await wrapPromise(
													getOAuthUrl(it.key).then((res) => {
														if (res.code === 0) {
															window.location.href = res.data.auth_url;
														}
													}),
												);
											}}
										>
											{isLoading ? (
												loadingIcon()
											) : (
												<img src={it.icon} alt={""} />
											)}
										</div>
									)}
								</LoadingHoc>
							);
						})}
					</div>

					<div className="text-sm flex items-center gap-1 md:mt-12 mt-6 md:mb-0 mb-8">
						<Star className="text-t2 size-3.5" />
						<span className="text-t2">
							{m.login_modal_address_publish_page()}
						</span>
						<a
							href={`https://peachlust.github.io?lang=${localStorage.getItem(
								"PARAGLIDE_LOCALE",
							)}`}
							className="text-c7"
						>
							https://peachlust.github.io?lang=
							{localStorage.getItem("PARAGLIDE_LOCALE")}
						</a>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
});
